import { useEffect, useState } from "react";
import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { MANAGE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", instrument: "", min_age: 7, active: true, public_description: "" };

function CurriculumDialog({ course, onClose }) {
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createName, setCreateName] = useState("");
  const [itemForm, setItemForm] = useState({ title: "", description: "", order: 0 });
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: list } = await api.get("/curriculums");
    const found = list.find((c) => c.course_id === course.id);
    if (found) {
      const { data: detail } = await api.get(`/curriculums/${found.id}`);
      setCurriculum(detail);
    } else {
      setCurriculum(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load().catch((e) => {
      toast.error(formatApiError(e));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id]);

  const createCurriculum = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/curriculums", { course_id: course.id, name: createName, active: true });
      toast.success("Currículo criado e ativado.");
      setCreateName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v) => {
    try {
      await api.put(`/curriculums/${curriculum.id}`, { active: v });
      toast.success(v ? "Currículo ativado." : "Currículo desativado — objetivos já copiados não são afetados.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/curriculums/${curriculum.id}/items`, {
        title: itemForm.title,
        description: itemForm.description,
        order: Number(itemForm.order),
      });
      toast.success("Item adicionado.");
      setItemForm({ title: "", description: "", order: Number(itemForm.order) + 1 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/curriculums/items/${editingItem.id}`, {
        title: editingItem.title,
        description: editingItem.description,
        order: Number(editingItem.order),
      });
      toast.success("Item atualizado.");
      setEditingItem(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async () => {
    try {
      await api.delete(`/curriculums/items/${deletingItem.id}`);
      toast.success("Item removido — objetivos já copiados não são afetados.");
      setDeletingItem(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeletingItem(null);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="curriculum-dialog">
        <DialogHeader>
          <DialogTitle>Currículo — {course.name}</DialogTitle>
          <DialogDescription>
            Template pedagógico do curso. Aplicar o currículo a uma matrícula cria uma cópia independente para o aluno.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : !curriculum ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground" data-testid="curriculum-empty">
              Este curso ainda não possui currículo. Crie um para padronizar os objetivos pedagógicos.
            </p>
            <form onSubmit={createCurriculum} className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Nome do currículo</Label>
                <Input required minLength={3} value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder={`Currículo de ${course.name}`} data-testid="curriculum-create-name-input" />
              </div>
              <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="curriculum-create-button">
                {saving ? "Criando..." : "Criar e ativar"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border border-border bg-zinc-50 p-3">
              <div>
                <p className="text-sm font-semibold">{curriculum.name}</p>
                <p className="text-xs text-muted-foreground">{curriculum.items?.length || 0} item(ns)</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={curriculum.active} onCheckedChange={toggleActive} data-testid="curriculum-active-switch" />
                <Label className="text-sm">{curriculum.active ? "Ativo" : "Inativo"}</Label>
              </div>
            </div>

            <div className="space-y-2" data-testid="curriculum-items-list">
              {(curriculum.items || []).map((item) =>
                editingItem?.id === item.id ? (
                  <form key={item.id} onSubmit={saveItem} className="space-y-2 border border-primary bg-white p-2" data-testid="curriculum-item-edit-form">
                    <div className="grid grid-cols-[1fr_90px] gap-2">
                      <Input required minLength={3} value={editingItem.title} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} data-testid="curriculum-item-edit-title" />
                      <Input type="number" min="0" value={editingItem.order} onChange={(e) => setEditingItem({ ...editingItem, order: e.target.value })} data-testid="curriculum-item-edit-order" />
                    </div>
                    <Textarea rows={2} value={editingItem.description || ""} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} data-testid="curriculum-item-edit-description" />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={saving} className="bg-primary text-primary-foreground" data-testid="curriculum-item-save-button">Salvar</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditingItem(null)}>Cancelar</Button>
                    </div>
                  </form>
                ) : (
                  <div key={item.id} className="flex flex-wrap items-center gap-2 border border-border p-2" data-testid={`curriculum-item-row-${item.id}`}>
                    <span className="text-xs font-bold text-muted-foreground">#{item.order}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{item.title}</p>
                      {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                    </div>
                    <Button variant="ghost" size="icon" title="Editar" onClick={() => setEditingItem({ ...item })} data-testid={`curriculum-item-edit-${item.id}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" title="Remover" onClick={() => setDeletingItem(item)} data-testid={`curriculum-item-delete-${item.id}`}>
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </div>
                )
              )}
            </div>

            <form onSubmit={addItem} className="space-y-2 border border-border bg-zinc-50 p-3" data-testid="curriculum-item-form">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Adicionar item</p>
              <div className="grid grid-cols-[1fr_90px] gap-2">
                <Input required minLength={3} placeholder="Título do objetivo" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} data-testid="curriculum-item-title-input" />
                <Input type="number" min="0" title="Ordem" value={itemForm.order} onChange={(e) => setItemForm({ ...itemForm, order: e.target.value })} data-testid="curriculum-item-order-input" />
              </div>
              <Textarea rows={2} placeholder="Descrição (opcional)" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} data-testid="curriculum-item-description-input" />
              <Button type="submit" size="sm" disabled={saving} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="curriculum-item-add-button">
                {saving ? "Adicionando..." : "Adicionar item"}
              </Button>
            </form>
          </div>
        )}
        <ConfirmDialog
          open={!!deletingItem}
          onOpenChange={() => setDeletingItem(null)}
          title="Remover item do currículo"
          description={`Remover "${deletingItem?.title}"? Objetivos já copiados para alunos não são afetados.`}
          onConfirm={removeItem}
          testId="curriculum-item-delete"
        />
      </DialogContent>
    </Dialog>
  );
}

export default function Courses() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user.role);
  const [courses, setCourses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [curriculumCourse, setCurriculumCourse] = useState(null);

  const load = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openDialog = (course) => {
    setEditing(course || null);
    setForm(course ? { name: course.name, instrument: course.instrument, min_age: course.min_age, active: course.active, public_description: course.public_description || "" } : EMPTY);
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, min_age: Number(form.min_age), instrument: form.instrument || form.name };
      if (editing) await api.put(`/courses/${editing.id}`, payload);
      else await api.post("/courses", payload);
      toast.success(editing ? "Curso atualizado." : "Curso criado.");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/courses/${deleting.id}`);
      toast.success("Curso excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Cursos</h1>
          <p className="mt-1 text-sm text-muted-foreground">Instrumentos e cursos oferecidos pelo IMJ.</p>
        </div>
        {canManage && (
          <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="course-add-button">
            <Plus size={16} className="mr-2" /> Novo curso
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Instrumento</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Idade mínima</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              {canManage && <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {courses.map((c) => (
              <TableRow key={c.id} className="transition-colors hover:bg-zinc-50" data-testid={`course-row-${c.name}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.instrument}</TableCell>
                <TableCell>{c.min_age} anos</TableCell>
                <TableCell>
                  <Badge variant={c.active ? "default" : "secondary"} className={c.active ? "bg-primary text-primary-foreground" : ""}>
                    {c.active ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" title="Currículo" onClick={() => setCurriculumCourse(c)} data-testid={`course-curriculum-${c.name}`}>
                      <BookOpen size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openDialog(c)} data-testid={`course-edit-${c.name}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleting(c)} data-testid={`course-delete-${c.name}`}>
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="course-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar curso" : "Novo curso"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="course-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome do curso</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="course-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Instrumento</Label>
              <Input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} placeholder="Igual ao nome, se vazio" data-testid="course-instrument-input" />
            </div>
            <div className="space-y-2">
              <Label>Idade mínima</Label>
              <Input type="number" min="0" max="100" required value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} data-testid="course-min-age-input" />
            </div>
            <div className="space-y-2">
              <Label>Descrição pública (site futuro, opcional)</Label>
              <Input value={form.public_description} onChange={(e) => setForm({ ...form, public_description: e.target.value })} data-testid="course-description-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="course-active-switch" />
              <Label>Curso ativo</Label>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="course-save-button">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir curso"
        description={`Deseja excluir o curso "${deleting?.name}"? Cursos vinculados a professores ou aulas não podem ser excluídos.`}
        onConfirm={remove}
        testId="course-delete-confirm"
      />

      {curriculumCourse && (
        <CurriculumDialog course={curriculumCourse} onClose={() => setCurriculumCourse(null)} />
      )}
    </div>
  );
}
